import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  PageHeader,
  Table,
  Button,
  Spin,
  Alert,
  Empty,
  Radio,
  Space,
  Modal,
  Descriptions,
  Tag,
} from "antd";
import {
  RightOutlined,
  WarningOutlined,
  PlusOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { InventoryClient, TemporaryPositionDto, PositionLabelClient } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";

const inventoryClient = new InventoryClient(new ApiConfig());
const positionLabelClient = new PositionLabelClient(new ApiConfig());

const TemporaryPositionsList: React.FC = () => {
  const history = useHistory();
  const [positions, setPositions] = React.useState<TemporaryPositionDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>("");
  const [creatingPosition, setCreatingPosition] = React.useState(false);
  const [selectedPrinter, setSelectedPrinter] = React.useState(6101);

  // Load temporary positions on mount
  React.useEffect(() => {
    loadTemporaryPositions();
  }, []);

  const loadTemporaryPositions = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await inventoryClient.getTemporaryPositions();
      setPositions(data);
    } catch (err) {
      console.error("Failed to load temporary positions:", err);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizePosition = (PositionId: string) => {
    history.push(`/temporary-positions/${PositionId}/organize`);
  };

  const handleCreateAndPrintPosition = async () => {
    setCreatingPosition(true);
    setError("");
    try {
      const response = await inventoryClient.createTemporaryPosition();
      const positionId = response.PositionId;

      try {
        await positionLabelClient.printTemporary(positionId, selectedPrinter, null);
        Modal.success({
          title: "Dočasná pozícia vytvorená",
          content: `Pozícia ${positionId} bola vytvorená a štítok bol odoslaný na tlačiareň.`,
          onOk: () => {
            loadTemporaryPositions();
          },
        });
      } catch (printErr) {
        console.error("Failed to print label:", printErr);
        Modal.warning({
          title: "Pozícia vytvorená, tlač zlyhala",
          content: `Pozícia ${positionId} bola vytvorená, ale tlač štítku zlyhala: ${getApiErrorMessage(printErr)}`,
          onOk: () => {
            loadTemporaryPositions();
          },
        });
      }
    } catch (err) {
      console.error("Failed to create temporary position:", err);
      setError(getApiErrorMessage(err));
    } finally {
      setCreatingPosition(false);
    }
  };

  const columns = [
    {
      title: "Pozícia",
      dataIndex: "PositionId",
      key: "PositionId",
      render: (PositionId: string) => (
        <strong style={{ fontSize: 18 }}>{PositionId}</strong>
      ),
    },
    {
      title: "Počet produktov",
      dataIndex: "ProductCount",
      key: "ProductCount",
      render: (count: number) => (
        <span style={{ fontSize: 16 }}>{count}</span>
      ),
    },
    {
      title: "Celkové množstvo",
      dataIndex: "TotalQuantity",
      key: "TotalQuantity",
      render: (quantity: number) => (
        <span style={{ fontSize: 16 }}>{quantity.toFixed(2)}</span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 150,
      render: (_: any, record: TemporaryPositionDto) => (
        <Button
          type="primary"
          icon={<RightOutlined />}
          onClick={() => handleOrganizePosition(record.PositionId)}
          size="large"
        >
          Zaradiť
        </Button>
      ),
    },
  ];

  const totalProducts = positions.reduce((sum, p) => sum + p.ProductCount, 0);
  const totalQuantity = positions.reduce((sum, p) => sum + p.TotalQuantity, 0);

  return (
    <div style={{ padding: "0 0 80px 0" }}>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Dočasné pozície"
      />

      {/* Error Alert */}
      {error && (
        <Alert
          message="Chyba"
          description={error}
          type="error"
          closable
          onClose={() => setError("")}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Warning if positions exist */}
      {positions.length > 0 && (
        <Alert
          message="Pozor!"
          description={`Máte ${positions.length} otvorených dočasných pozícií. Zaraďte produkty do trvalých políc čo najskôr.`}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Instructions */}
      <Alert
        message="Vytvorenie novej dočasnej pozície"
        description={
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <label style={{ marginRight: 16, fontWeight: 500 }}>Tlačiareň:</label>
              <Radio.Group
                value={selectedPrinter}
                onChange={(e) => setSelectedPrinter(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value={6101}>SKLAD (6101)</Radio.Button>
                <Radio.Button value={6102}>HALA (6102)</Radio.Button>
              </Radio.Group>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleCreateAndPrintPosition}
              loading={creatingPosition}
              block
            >
              <PrinterOutlined style={{ marginLeft: 8 }} />
              {" "}Vytvoriť pozíciu a vytlačiť štítok
            </Button>
          </Space>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Statistics */}
      <Descriptions column={{ xs: 1, sm: 3 }} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Otvorené dočasné pozície">
          <Tag color={positions.length > 0 ? "red" : "green"} style={{ fontSize: 16, padding: "4px 12px" }}>
            {positions.length}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Celkový počet produktov">
          <strong style={{ fontSize: 16 }}>{totalProducts}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Celkové množstvo">
          <strong style={{ fontSize: 16 }}>{totalQuantity.toFixed(2)}</strong>
        </Descriptions.Item>
      </Descriptions>

      {/* Positions Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={positions}
          columns={columns}
          rowKey="PositionId"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Celkom ${total} pozícií`,
          }}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Žiadne otvorené dočasné pozície"
              />
            ),
          }}
        />
      </Spin>

      <Button
        style={{ marginTop: 16 }}
        onClick={loadTemporaryPositions}
        loading={loading}
      >
        Obnoviť
      </Button>
    </div>
  );
};

export default TemporaryPositionsList;
